import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { AlertTriangle, CheckCircle2, Lock, LockOpen, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { AccountSelect, PageTitle, sortAccounts } from './shared';

const ROLE_LABEL = {
  bank: 'Main bank account (payments land here)',
  ar: 'Accounts receivable',
  revenue: 'Service revenue',
  ap: 'Accounts payable',
  creditCard: 'Business credit card',
  salesTax: 'Sales tax payable',
  payrollExpense: 'Payroll expense',
  payrollLiabilities: 'Payroll liabilities (withholding)',
  fixedAssets: 'Vehicles & equipment',
  accumDepreciation: 'Accumulated depreciation',
  depreciationExpense: 'Depreciation expense',
  openingEquity: 'Opening balance equity',
  uncategorizedIncome: 'Uncategorized income',
  uncategorizedExpense: 'Uncategorized expense',
};
// The expense categories offered on the Expenses screen.
const EXPENSE_CATEGORIES = ['Chemicals & Supplies', 'Fuel', 'Payroll', 'Insurance', 'Office Supplies', 'Equipment', 'Vehicle Maintenance', 'Marketing', 'Software', 'Utilities', 'Other'];

export default function CloseBooks() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    ledgerApi.settings().then((d) => { setData(d); setForm(d.settings); }).catch((e) => toast.error(apiError(e, 'Could not load settings')));
    ledgerApi.health().then(setHealth).catch(() => setHealth(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, msg) => {
    setBusy(true);
    try {
      const next = { ...form, ...patch };
      await ledgerApi.saveSettings(next);
      toast.success(msg || 'Saved');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const setup = async () => {
    setBusy(true);
    try {
      const r = await ledgerApi.setup();
      toast.success(r.added.length ? `Added ${r.added.length} accounts` : 'Your chart already has every account the books need');
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data || !form) return <div className="p-8 text-slate-500 text-sm">Loading…</div>;
  const accounts = sortAccounts(data.accounts);
  const expenseAccounts = (a) => ['Expense', 'Cost of Goods Sold', 'Other Expense', 'Fixed Asset'].includes(a.type);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-close">
      <PageTitle title="Close Books" subtitle="Lock finished periods, and tell the books which account each kind of record posts to." />

      {health ? (
        <Card className={`p-4 ${health.missingRoles.length || health.skippedCount || !health.balanced ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <div className="flex items-start gap-3">
            {health.missingRoles.length || health.skippedCount || !health.balanced ? <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />}
            <div className="text-sm space-y-1 flex-1">
              <div className="font-semibold text-slate-900">{health.entryCount} journal entries · {health.balanced ? 'trial balance in balance' : 'trial balance OUT of balance'}</div>
              {health.missingRoles.length ? <div>Missing accounts: {health.missingRoles.map((r) => ROLE_LABEL[r] || r).join(', ')}. Records that need them are not posted yet.</div> : null}
              {health.skippedCount ? (
                <details><summary className="cursor-pointer">{health.skippedCount} record{health.skippedCount === 1 ? '' : 's'} could not be posted</summary>
                  <ul className="mt-1 text-xs text-slate-700 max-h-48 overflow-y-auto">{health.skipped.map((s, i) => <li key={i}>{s.kind} {s.id}: {s.reason}</li>)}</ul>
                </details>
              ) : null}
            </div>
            {health.missingRoles.length ? <Button size="sm" onClick={setup} disabled={busy} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1"><Wrench className="w-3.5 h-3.5" />Add missing accounts</Button> : null}
          </div>
        </Card>
      ) : null}

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 border-slate-200 space-y-4">
          <div className="flex items-center gap-2 font-bold text-slate-900">{form.lockDate ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}Closing date</div>
          <p className="text-sm text-slate-600">Invoices, payments, expenses, bills and journal entries dated on or before this day can't be added, changed or deleted. Close a month once it's reconciled and reviewed.</p>
          <div className="flex items-end gap-2">
            <div><Label className="text-xs text-slate-500">Books closed through</Label><Input type="date" value={form.lockDate || ''} onChange={(e) => setForm({ ...form, lockDate: e.target.value })} className="mt-1 h-9 w-44" data-testid="lock-date" /></div>
            <Button className="h-9 bg-[#8BC53F] hover:bg-[#6FA82C]" disabled={busy} onClick={() => save({}, form.lockDate ? `Books closed through ${form.lockDate}` : 'Books reopened')} data-testid="save-lock">Save</Button>
            {data.settings.lockDate ? <Button variant="outline" className="h-9" disabled={busy} onClick={() => save({ lockDate: '' }, 'Books reopened')}>Reopen all</Button> : null}
          </div>
          {data.settings.lockDate ? <Badge className="border-0 bg-slate-900 text-white">Closed through {data.settings.lockDate}</Badge> : <Badge className="border-0 bg-slate-100 text-slate-600">No period closed</Badge>}
        </Card>

        <Card className="p-5 border-slate-200 space-y-4">
          <div className="font-bold text-slate-900">Books start date</div>
          <p className="text-sm text-slate-600">Leave empty to build the books from every record on file. If an accountant gives you opening balances as of a date, enter that date here and post the balances as one journal entry to Opening Balance Equity — older records then stay out of the books.</p>
          <div className="flex items-end gap-2">
            <div><Label className="text-xs text-slate-500">Start the books on</Label><Input type="date" value={form.startDate || ''} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="mt-1 h-9 w-44" /></div>
            <Button className="h-9 bg-[#8BC53F] hover:bg-[#6FA82C]" disabled={busy} onClick={() => save({}, 'Start date saved')}>Save</Button>
          </div>
        </Card>
      </div>

      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="font-bold text-slate-900">Where records post</div>
          <Button variant="outline" size="sm" onClick={setup} disabled={busy} className="gap-1"><Wrench className="w-3.5 h-3.5" />Add any missing standard accounts</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">
          {data.roles.map((role) => (
            <div key={role} className="flex items-center justify-between gap-3">
              <Label className="text-sm text-slate-700">{ROLE_LABEL[role] || role}</Label>
              <AccountSelect accounts={accounts} value={form.accounts[role]} onChange={(v) => setForm({ ...form, accounts: { ...form.accounts, [role]: v } })} className="h-9 w-64" />
            </div>
          ))}
        </div>
        <div className="font-bold text-slate-900 pt-2">Expense categories</div>
        <p className="text-sm text-slate-600 -mt-2">By default a category posts to the account with the same name. Pick a different account to override it.</p>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">
          {EXPENSE_CATEGORIES.map((cat) => (
            <div key={cat} className="flex items-center justify-between gap-3">
              <Label className="text-sm text-slate-700">{cat}</Label>
              <AccountSelect accounts={accounts} filter={expenseAccounts} placeholder="Same-name account" value={form.categoryMap[cat]} onChange={(v) => setForm({ ...form, categoryMap: { ...form.categoryMap, [cat]: v } })} className="h-9 w-64" />
            </div>
          ))}
        </div>
        <div className="flex justify-end"><Button className="bg-[#8BC53F] hover:bg-[#6FA82C]" disabled={busy} onClick={() => save({}, 'Account mapping saved')}>Save mapping</Button></div>
      </Card>
    </div>
  );
}
